/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { forwardRef, useRef, useEffect } from 'react';
import Link from 'next/link'; // eslint-disable-line no-restricted-imports
import type { Url } from '../next-types';
// TODO: refactor this module to fix eslint violation
/* eslint-disable react/jsx-no-target-blank */

export type SmartLinkProps = {
  samePage?: boolean;
  noReferrer?: boolean;
  children?: React.ReactNode;
  setFocusToAnchorTag?: boolean;
  to: Url;
  isPhoneNumber?: boolean;
  ariaLabel?: string;
  scroll?: boolean;
  handleClick?: () => void;
  externalLinkLabel?: string;
  /**
   * Masked URL to display in the browser address bar (Next.js `as` prop).
   *
   * **WARNING: This is an anti-pattern and should be used sparingly.**
   *
   * Using masked URLs can cause issues with:
   * - SEO: Search engines may not properly index the content
   * - User experience: Users cannot see the actual URL they're navigating to
   * - Accessibility: Screen readers and assistive technologies may not announce the correct destination
   * - Browser history: Back/forward navigation may behave unexpectedly
   *
   * This prop should only be used when you need to:
   * 1. Pass query parameters to the destination page via router.query
   * 2. Hide those parameters from the URL bar to maintain clean URLs for users/bots
   *
   * @example
   * // Shows '/products/spark-plug' in browser, but router.query contains productPartGroupId
   * <SmartLink
   *   to="/products/spark-plug?productPartGroupId=123"
   *   maskedURL="/products/spark-plug"
   * >
   *   Product Link
   * </SmartLink>
   */
  maskedURL?: string;
} & React.ComponentPropsWithoutRef<'a'>;

export const getPath = (val: Url) => (typeof val === 'string' ? val : val?.pathname);

/**
 * Matches URLs starting with '/videos' or '/company'
 */
const EXTERNAL_SITE_REGEX = /^(?:https?:\/\/[^/]+)?\/(videos|company)(?:\/|$)/;

export const SmartLink = forwardRef<HTMLAnchorElement, SmartLinkProps>(function SmartLinkComp(
  {
    to,
    children,
    setFocusToAnchorTag,
    samePage,
    noReferrer = true,
    href,
    isPhoneNumber = false,
    ariaLabel,
    scroll = true,
    handleClick,
    externalLinkLabel,
    maskedURL,
    ...rest
  }: SmartLinkProps,
  forwardedRef,
) {
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!forwardedRef) {
      return;
    }
    if (typeof forwardedRef === 'function') {
      forwardedRef(ref.current);
    } else {
      forwardedRef.current = ref.current;
    }
  }, [forwardedRef]);

  useEffect(() => {
    if (setFocusToAnchorTag) {
      ref.current?.focus();
    }
  }, [setFocusToAnchorTag]);

  const targetPath = href ? getPath(href) : getPath(to); // If it's a relative url such as '/path', 'path' and does not contain a protocol we can assume it is internal.

  if (isPhoneNumber) {
    return (
      <a ref={ref} href={`tel:${targetPath}`} {...rest}>
        {children}
      </a>
    );
  }

  if (typeof targetPath === 'string' && targetPath.includes('://')) {
    const samePageLink = samePage && !EXTERNAL_SITE_REGEX.test(targetPath);
    return (
      <a
        ref={ref}
        href={targetPath}
        aria-label={ariaLabel}
        target={samePageLink ? '' : '_blank'}
        rel={noReferrer ? 'noopener noreferrer' : undefined}
        {...rest}
        onClick={handleClick}
      >
        {children}
        {!samePageLink && externalLinkLabel ? (
          <span className="sr-only">{externalLinkLabel}</span>
        ) : null}
      </a>
    );
  }

  const samePageLink = samePage || !EXTERNAL_SITE_REGEX.test(targetPath);

  return (
    <Link
      href={targetPath || '#'}
      as={maskedURL}
      prefetch={false}
      scroll={scroll}
      ref={ref}
      aria-label={ariaLabel}
      target={samePageLink ? '' : '_blank'}
      onClick={handleClick}
      {...rest}
    >
      {children}
      {!samePageLink && externalLinkLabel ? (
        <span className="sr-only">{externalLinkLabel}</span>
      ) : null}
    </Link>
  );
});
